import React, { useEffect, useState } from "react";
import api from "../../../lib/api";
import { AdminButton, AdminInput, AdminCard } from "../AdminUI";
import { Plus, Trash2, Loader2, Pencil, Check, X, IceCream } from "lucide-react";
import { toast } from "sonner";

const FlavorsTab = () => {
    const [flavors, setFlavors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [editing, setEditing] = useState(null);

    const reload = async () => {
        const r = await api.get("/flavors");
        setFlavors(r.data);
        setLoading(false);
    };

    useEffect(() => { reload(); }, []);

    const add = async () => {
        if (!newName.trim()) return toast.error("Name ist erforderlich");
        try {
            await api.post("/flavors", { name: newName.trim(), sort_order: flavors.length });
            setNewName("");
            await reload();
            toast.success("Sorte hinzugefügt");
        } catch { toast.error("Fehler"); }
    };

    const save = async (f) => {
        if (!f.name.trim()) return toast.error("Name ist erforderlich");
        await api.put(`/flavors/${f.id}`, { name: f.name.trim(), sort_order: f.sort_order });
        setEditing(null);
        await reload();
        toast.success("Sorte aktualisiert");
    };

    const remove = async (id) => {
        if (!window.confirm("Sorte löschen? (Sie verschwindet auch aus dem 'Heutiges Softeis' Dropdown.)")) return;
        await api.delete(`/flavors/${id}`);
        await reload();
        toast.success("Sorte gelöscht");
    };

    if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    return (
        <div className="space-y-6 max-w-2xl" data-testid="flavors-tab">
            <AdminCard className="space-y-3">
                <h3 className="font-serif text-xl text-deep-green">Neue Sorte anlegen</h3>
                <p className="text-sm text-deep-green/65">
                    Legen Sie hier einmalig alle Eissorten an, die Sie grundsätzlich anbieten.
                    Im Bereich <span className="font-medium text-deep-green">"Heutiges Softeis"</span> können Sie dann aus diesen Sorten auswählen.
                </p>
                <div className="flex gap-2">
                    <AdminInput
                        placeholder="z.B. Pistazie"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                        className="flex-1"
                        data-testid="new-flavor-name"
                    />
                    <AdminButton onClick={add} data-testid="new-flavor-add">
                        <Plus className="w-4 h-4" /> Hinzufügen
                    </AdminButton>
                </div>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-deep-green mb-4">Alle Sorten ({flavors.length})</h3>
                {flavors.length === 0 ? (
                    <div className="text-center py-10 text-deep-green/60">
                        <IceCream className="w-8 h-8 mx-auto mb-2 text-leaf-green" />
                        Noch keine Sorten angelegt.
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {flavors.map((f) => (
                            <li
                                key={f.id}
                                className="flex items-center gap-3 p-3 rounded-xl bg-cream-dark/40"
                                data-testid={`flavor-row-${f.id}`}
                            >
                                {editing?.id === f.id ? (
                                    <>
                                        <AdminInput
                                            value={editing.name}
                                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                            onKeyDown={(e) => { if (e.key === "Enter") save(editing); }}
                                            className="flex-1"
                                            autoFocus
                                        />
                                        <AdminButton onClick={() => save(editing)}><Check className="w-4 h-4" /></AdminButton>
                                        <AdminButton variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></AdminButton>
                                    </>
                                ) : (
                                    <>
                                        <IceCream className="w-5 h-5 text-garden-green-dark flex-shrink-0" />
                                        <span className="flex-1 font-medium text-deep-green">{f.name}</span>
                                        <AdminButton variant="ghost" onClick={() => setEditing(f)} data-testid={`flavor-edit-${f.id}`}>
                                            <Pencil className="w-4 h-4" />
                                        </AdminButton>
                                        <AdminButton variant="danger" onClick={() => remove(f.id)} data-testid={`flavor-delete-${f.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </AdminButton>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </AdminCard>
        </div>
    );
};

export default FlavorsTab;
